/**
 * Checkout.
 *
 * Two decisions are made here — when the customer wants the food, and how to
 * reach them — and neither is trusted to the client. Every total on this screen
 * comes from `quote_order`, and the order itself is created by `place_order`,
 * which recomputes the same numbers, revalidates availability, and reserves
 * inventory in one transaction.
 *
 * The quote is refreshed whenever the cart or the chosen time changes, so the
 * price shown is never stale by more than one round trip.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ActivityIndicator, Pressable, View, type TextInput } from 'react-native';
import {
  checkoutSchema,
  phoneSchema,
  phoneHintFor,
  phonePlaceholder,
  formatPhoneFor,
  withDialCode,
  fieldErrors,
  formatClock,
  formatDayTime,
  formatMoney,
  localDateKey,
  type OrderQuote,

  countryName,} from '@yummy/shared';
import { fetchAvailableSlots, placeOrder, quoteOrder, recallContact, rememberContact } from '@yummy/db';
import {
  Button,
  Callout,
  ChoiceField,
  ErrorBanner,
  LoadingState,
  Row,
  Screen,
  Section,
  Stack,
  StatRow,
  Stepper,
  Text,
  TextField,
  cn,
  useAction,
  useAsync,
  useDebounced,
  useNow,
  useT,
  useTheme,
  useToast,

  useLocale,} from '@yummy/ui-rnr';

import { cartPayload, useCart } from '../app/cart';
import { useBackTo } from '../app/navigation';
import { forgetStorefront, loadRestaurant, recallTableToken } from '../app/storefront';
import { AppBar } from '../components/AppBar';
import { CheckoutSkeleton } from '../components/CheckoutSkeleton';
import { FooterBar } from '../components/FooterBar';
import { SlotPicker } from '../components/SlotPicker';
import { supabase } from '../lib/supabase';

type FulfilmentMode = 'asap' | 'scheduled';

/**
 * Clearance between the floating header and the first order line, in points.
 *
 * The header is out of the flow and the content is inset by exactly its height,
 * so the list began flush against the bar and the first line read as part of it.
 * The padding is measured from the bar, but the eye measures from the controls
 * inside it: a 45pt control centred in a 64pt bar leaves 9pt of empty bar under
 * it, so this is the house inset of 16 with the bar's own slack already counted.
 */
const HEADER_GAP = 16;

/**
 * Collects pickup time and contact details, then places the order.
 *
 * @param props.overlay Whether this is the dialog over the menu. A dialog closes
 *   with an X and carries no title, and shows its shape as a skeleton while it
 *   loads; the standalone page, reached directly or after a refresh, keeps the
 *   back control and the screen name.
 * @returns The checkout screen.
 */
export function CheckoutScreen({ overlay = false }: { overlay?: boolean }) {
  const { slug = '' } = useParams<{ slug: string }>();
  const theme = useTheme();
  const t = useT();
  const { locale } = useLocale();
  const navigate = useNavigate();
  // Back to the menu it was opened from, wherever that was — and to the
  // restaurant's own page when checkout is the first thing opened, which is what
  // a shared link produces.
  const goBack = useBackTo(`/r/${slug}`);
  const toast = useToast();
  const cart = useCart();
  const place = useAction();

  /**
   * Ticking, so a time the customer is sitting on can go stale on its own. The
   * line under the offered times is an instant from the server; this is the
   * clock it is read against.
   */
  const now = useNow();

  const [mode, setMode] = useState<FulfilmentMode>('asap');
  const [slot, setSlot] = useState<string | null>(null);
  /**
   * Whether the customer has picked a time themselves. Until they have, the
   * selection follows the kitchen's estimate; after that it is theirs, and the
   * warning is all that is left to say.
   */
  const [slotTouched, setSlotTouched] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', note: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});

  /**
   * The detail fields, so a failed submit can put the first one that needs an
   * answer in front of the customer instead of only marking it where it sits.
   */
  const fields = {
    name: useRef<TextInput>(null),
    phone: useRef<TextInput>(null),
    email: useRef<TextInput>(null),
    note: useRef<TextInput>(null),
  };

  // ---------------------------------------------------------------------------
  // Restaurant and available times
  // ---------------------------------------------------------------------------

  const page = useAsync(async () => {
    // Shared with the menu, which has almost certainly just loaded it.
    const restaurant = await loadRestaurant(slug);
    if (!restaurant) throw new Error(t('customer.menu.gone'));
    const slots = await fetchAvailableSlots(supabase, restaurant.id, 7);
    return { restaurant, slots };
  }, [slug]);

  // ---------------------------------------------------------------------------
  // Contact details remembered from the last order on this device
  // ---------------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    void recallContact(supabase).then((contact) => {
      if (cancelled) return;
      setForm((current) => ({
        ...current,
        name: current.name || contact.name || '',
        phone: current.phone || contact.phone || '',
      }));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Server quote
  // ---------------------------------------------------------------------------

  const scheduledFor = mode === 'scheduled' ? slot : null;

  /**
   * Where the customer appears to be, when that is not where the restaurant is.
   *
   * Resolved server-side from the request headers, so it cannot be read here —
   * this asks. Informational: the order still places, and the merchant sees the
   * same fact on their card.
   */
  const [elsewhere, setElsewhere] = useState<{ from: string; at: string } | null>(null);
  /** Where the order is collected, for the phone hint. */
  const [restaurantCountry, setRestaurantCountry] = useState<string | null>(null);
  const restaurantId = page.data?.restaurant.id;
  useEffect(() => {
    if (!restaurantId) return;
    let alive = true;
    void supabase
      .rpc('region_check', { p_restaurant_id: restaurantId })
      .then(({ data }) => {
        const result = data as { country: string | null; restaurant_country: string | null; elsewhere: boolean } | null;
        if (!alive) return;
          setRestaurantCountry(result?.restaurant_country ?? null);
          // Always assigned, including to null. Setting it only when the mismatch
          // is true leaves a stale warning behind when the customer moves to a
          // restaurant in their own country — one naming the wrong country, with
          // no way to dismiss it.
          setElsewhere(
            result?.elsewhere && result.country && result.restaurant_country
              ? { from: result.country, at: result.restaurant_country }
              : null,
          );
      });
    return () => {
      alive = false;
    };
  }, [restaurantId, supabase]);
  const payload = useMemo(() => cartPayload(cart.lines), [cart.lines]);

  // Collapse rapid stepper taps into one request.
  const debouncedPayload = useDebounced(payload, 300);
  const cartKey = JSON.stringify(debouncedPayload);

  const quote = useAsync<OrderQuote | null>(async () => {
    const restaurant = page.data?.restaurant;
    if (!restaurant || debouncedPayload.length === 0) return null;
    return quoteOrder(supabase, restaurant.id, debouncedPayload, scheduledFor);
  }, [cartKey, scheduledFor, page.data?.restaurant.id]);

  /**
   * Open on Schedule when the restaurant is shut.
   *
   * A closed restaurant takes no ASAP order — `place_order` refuses it, and the
   * ASAP card is drawn as one the customer cannot take — so As soon as possible
   * is a choice they cannot use. Seeding the mode once, when the restaurant
   * first arrives, opens the screen on the choice that works without taking the
   * other away: the ASAP card is still drawn, still marked closed, for anyone
   * who wants to see it.
   *
   * Once per mount rather than on every render where the mode is ASAP: the
   * customer who switches back to it would otherwise be snapped forward again,
   * the effect arguing with the control it means to seed.
   */
  const seededFulfilment = useRef(false);
  useEffect(() => {
    if (seededFulfilment.current || !page.data) return;
    seededFulfilment.current = true;
    const { restaurant, slots } = page.data;
    if (restaurant.is_open_now) return;
    setMode('scheduled');
    // A time as well as a mode, so the schedule opens with one chosen rather
    // than asking for the decision the switch meant to spare. The estimate
    // corrects it once the quote lands, exactly as it does for a tapped
    // Schedule.
    setSlot((current) => current ?? slots[0] ?? null);
  }, [page.data]);

  // A quote that came back unhappy while the restaurant is shut is not a dead
  // end either: scheduling is the answer, so the mode need not wait to be told.
  // The seed above covers the ordinary closure, before any quote is asked for;
  // this is the one the server refused.
  useEffect(() => {
    if (mode === 'asap' && quote.error && page.data && !page.data.restaurant.is_open_now) {
      setMode('scheduled');
    }
  }, [mode, quote.error, page.data]);

  /**
   * The soonest this basket can be ready — the line the checkout draws under the
   * offered times.
   *
   * A fact about the basket rather than about the time chosen, so it holds in
   * either mode and is known before a schedule is picked. `quote.data` survives a
   * refetch, so the line does not blink while a changed cart is priced; before
   * the first quote there is no line, which is the right amount of opinion.
   *
   * Nothing is refused on it. `min_lead_minutes` is still the only thing that can
   * withhold a time, and a customer who wants an impossible one may have it,
   * having been told.
   */
  const earliestAt = quote.data?.earliest_scheduled_for
    ? Date.parse(quote.data.earliest_scheduled_for)
    : null;

  /** The first offered time at or after that line, or null while there is none. */
  const firstComfortableIn = (list: string[]): string | null =>
    earliestAt === null
      ? null
      : (list.find((candidate) => Date.parse(candidate) >= earliestAt) ?? null);

  /**
   * Default to the soonest time the kitchen can actually make.
   *
   * The quote is what knows that, and it can land after the customer has already
   * switched to Schedule and been handed `slots[0]`. So the default keeps
   * following the line until the customer picks a time themselves; after that the
   * selection is theirs and the warning is all that is left to say.
   */
  useEffect(() => {
    if (mode !== 'scheduled' || slotTouched || earliestAt === null) return;
    const list = page.data?.slots ?? [];
    const next = list.find((candidate) => Date.parse(candidate) >= earliestAt) ?? null;
    if (next && next !== slot) setSlot(next);
  }, [mode, slotTouched, slot, earliestAt, page.data]);

  // ---------------------------------------------------------------------------
  // Empty cart
  // ---------------------------------------------------------------------------

  // Placing an order empties the cart, which would otherwise trip the redirect
  // below and bounce the customer back to the menu instead of their tracking
  // screen. This flag latches for the lifetime of the screen.
  const submitted = useRef(false);

  useEffect(() => {
    if (submitted.current) return;
    if (cart.lines.length === 0) {
      navigate(`/r/${slug}`, { replace: true });
    }
  }, [cart.lines.length, navigate, slug]);

  // ---------------------------------------------------------------------------
  // Placing the order
  // ---------------------------------------------------------------------------

  /**
   * A refusal about the time is answered by changing the time.
   *
   * `place_order` refuses an ASAP order from a restaurant that is shut, and the
   * sentence it returns names the way out — schedule a pickup. Leaving it over
   * the details after the customer has taken that step reports a problem they
   * have just solved, so the choice clears it: the mode, and the slot itself
   * when it moves under a schedule already chosen.
   */
  const { clearError: clearPlaceError } = place;
  useEffect(() => {
    clearPlaceError();
  }, [mode, slot, clearPlaceError]);

  const submit = useCallback(async () => {
    const restaurant = page.data?.restaurant;
    if (!restaurant) return;

    const parsed = checkoutSchema.safeParse(form);
    if (!parsed.success) {
      setErrors(fieldErrors(parsed.error));
      // Name and mobile number are the only required fields, and both sit below
      // the slot grid — so submitting with them empty used to mark fields a
      // screen away and read as the button doing nothing. Go to the first one.
      const first = parsed.error.issues[0]?.path[0];
      const target = typeof first === 'string' ? fields[first as keyof typeof fields] : undefined;
      if (target) {
        const reveal = () => {
          const node = target.current as unknown as HTMLElement | null;
          node?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
          node?.focus?.();
        };
        // A frame later, so the error text added by the render above is part of
        // the layout the field is measured in.
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(reveal);
        else reveal();
      }
      return;
    }
    setErrors({});

    if (mode === 'scheduled' && !slot) {
      setErrors({ slot: t('customer.checkout.chooseTime') });
      return;
    }

    const order = await place.run(() =>
      placeOrder(supabase, {
        restaurantId: restaurant.id,
        items: cartPayload(cart.lines),
        customerName: parsed.data.name,
        customerPhone: withDialCode(parsed.data.phone, restaurantCountry),
        customerEmail: parsed.data.email,
        note: parsed.data.note,
        scheduledFor,
        tableToken: recallTableToken(),
        source: recallTableToken() ? 'qr' : 'link',
      }),
    );

    if (!order) return;

    submitted.current = true;

    // Best-effort: speeds up the next order from this device.
    void rememberContact(supabase, withDialCode(parsed.data.phone, restaurantCountry), parsed.data.name);

    cart.clear();
    // The order just consumed stock, so the menu's copy of this restaurant is
    // out of date — a dish on its last portion would still read as available.
    forgetStorefront(slug);
    toast.show(
      parsed.data.email
        ? t('customer.checkout.orderPlacedWithReceipt', {
            number: `#${order.order_number}`,
            email: parsed.data.email,
          })
        : t('customer.checkout.orderPlaced', { number: `#${order.order_number}` }),
      { tone: 'success' },
    );
    // Displaces the checkout entry. Overlaying the menu, that entry sat on the
    // menu, so the order inherits it as the screen behind — which is what lets
    // its Back to menu be a step back rather than another copy of the menu.
    navigate(`/order/${order.id}`, {
      replace: true,
      ...(overlay ? { state: { from: 'menu' } } : {}),
    });
  }, [page.data, form, mode, slot, place, cart, scheduledFor, toast, navigate, t, overlay]);

  // ---------------------------------------------------------------------------

  // The dialog closes with an X where a page has a back control. Both name the
  // screen and, beneath it, the restaurant the order is from — the name the menu
  // gave, kept on screen all the way down to Place order. Held from the first
  // paint, so the checkout does not change height when the restaurant arrives.
  const subtitle = page.data?.restaurant.name ?? '';
  const header = overlay ? (
    <AppBar
      title={t('customer.checkout.title')}
      subtitle={subtitle}
      onClose={goBack}
      preferences={false}
    />
  ) : (
    <AppBar
      title={t('customer.checkout.title')}
      subtitle={subtitle}
      onBack={goBack}
      preferences={false}
    />
  );

  if (page.initial) {
    return (
      <Screen autoHideHeader={overlay} header={overlay ? header : undefined} headerGap={HEADER_GAP}>
        {/* The dialog shows its shape while it waits, so the sheet arrives with
            structure rather than an empty frame; the page keeps the spinner,
            because it is not a sheet arriving over anything. */}
        {overlay ? (
          <CheckoutSkeleton />
        ) : (
          <LoadingState message={t('customer.checkout.loading')} />
        )}
      </Screen>
    );
  }

  if (page.error || !page.data) {
    return (
      <Screen autoHideHeader={overlay} header={overlay ? header : undefined} headerGap={HEADER_GAP}>
        <ErrorBanner
          title={t('customer.checkout.unavailable')}
          message={page.error ?? t('common.somethingWentWrong')}
          onRetry={page.reload}
        />
      </Screen>
    );
  }

  const { restaurant, slots } = page.data;
  const current = quote.data;
  const currency = restaurant.currency;

  /**
   * Whether the restaurant is outside its opening hours.
   *
   * An ASAP order cannot be placed at all then — `place_order` refuses it — so
   * the estimate the quote returns is a promise the kitchen cannot keep and is
   * withheld entirely, and As soon as possible is offered as a card the customer
   * cannot take rather than as one that fails at the button.
   */
  const isClosed = !restaurant.is_open_now;

  /**
   * Whether the chosen time is one the kitchen cannot make, as far as the
   * estimate knows. Informational: nothing here refuses anything.
   */
  const slotIsTight =
    mode === 'scheduled' &&
    slot !== null &&
    earliestAt !== null &&
    Date.parse(slot) < earliestAt;

  /** Where to move to instead, which is the line's own answer and may be another day. */
  const suggestion = firstComfortableIn(slots);
  /*
   * Named by the clock when it is the same day as the one chosen, and by the day
   * when it is not — "Use 17:00" is enough inside a day and wrong across one.
   */
  const suggestionLabel =
    suggestion === null
      ? null
      : slot !== null &&
          localDateKey(new Date(suggestion), restaurant.timezone) ===
            localDateKey(new Date(slot), restaurant.timezone)
        ? formatClock(suggestion, restaurant.timezone)
        : formatDayTime(suggestion, restaurant.timezone, now, t);

  // Fall back to locally-known prices until the first quote lands, so the bar
  // is never blank.
  const displaySubtotal = current?.subtotal_cents ?? cart.subtotalCents;
  const displayTotal = current?.total_cents ?? cart.subtotalCents;

  const placeLabel = place.pending
    ? t('customer.checkout.placing')
    : t('customer.checkout.placeOrder');

  return (
    <Screen
      autoHideHeader
      header={header}
      headerGap={HEADER_GAP}
      footer={
        <FooterBar>
          <Stack gap="sm">
        {elsewhere ? (
          <Callout
            tone="warning"
            title={t('customer.checkout.elsewhereTitle', { country: countryName(elsewhere.at, locale) })}
            message={t('customer.checkout.elsewhere', { country: countryName(elsewhere.from, locale) })}
            size="base"
          />
        ) : null}
            {quote.error ? (
              <Text numberOfLines={2} className="text-destructive text-sm">
                {quote.error}
              </Text>
            ) : null}
            <Row spread gap="md" align="center">
              <Stack gap="xs">
                {/* The caption names the number and never changes: a word like
                    "Updating…" swapped in here reads as the label of a figure
                    that is still on screen, and it flashed on every mode and
                    slot change, where the total cannot move at all. */}
                <Text className="text-muted-foreground text-sm">{t('common.total')}</Text>
                {/* The same type as the menu's cart bar: this is the same number
                    in the same place, and it should not change size on the way
                    from the menu to checkout. Dimmed while a re-price is in
                    flight, so the number carries that state rather than the
                    label. */}
                <Text
                  className={cn('text-lg font-bold tabular-nums', quote.loading && 'opacity-50')}>
                  {formatMoney(displayTotal, currency)}
                </Text>
              </Stack>
              <Button
                onPress={submit}
                disabled={place.pending || !current || cart.lines.length === 0}
                size="lg"
                accessibilityLabel={placeLabel}
                aria-busy={place.pending}
                className="min-w-[190px]">
                {place.pending ? (
                  <ActivityIndicator color={theme.palette.primaryForeground} size="small" />
                ) : (
                  <Text>{placeLabel}</Text>
                )}
              </Button>
            </Row>
          </Stack>
        </FooterBar>
      }>
      <Stack gap="xl">
        {/* No heading: the bar names the screen and the restaurant it is for, so
            the first section is the lines themselves. */}
        <Stack gap="md">
          {cart.lines.map((line) => (
            <Row key={line.itemId} spread gap="md" align="center">
              <Stack gap="xs" className="flex-1">
                <Text numberOfLines={2} className="text-base font-semibold">
                  {line.name}
                  {/* The extras are part of what was ordered, so they are part
                      of what is shown — for the customer and for the kitchen. */}
                  {(line.options ?? []).length > 0
                    ? ` · ${(line.options ?? []).map((option) => option.name).join(', ')}`
                    : ''}
                </Text>
                <Text className="text-muted-foreground text-sm">
                  {formatMoney(line.unitPriceCents, currency)} each
                </Text>
              </Stack>
              <Stepper
                floor={line.minQuantity}
                value={line.quantity}
                onChange={(quantity) => cart.setQuantity(line.itemId, line.options ?? [], quantity)}
                min={0}
                max={99}
                size="sm"
              />
            </Row>
          ))}
        </Stack>

        <Section title={t('customer.checkout.pickupTime')}>
          <Stack gap="md">
            <ChoiceField
              options={[
                {
                  value: 'asap',
                  label: t('customer.checkout.asap'),
                  // A shut restaurant cannot take this order, and the card says so
                  // itself rather than letting the button discover it.
                  disabled: isClosed,
                  description: restaurant.is_open_now
                    ? t('customer.checkout.asapHint')
                    : t('customer.checkout.asapClosed'),
                },
                {
                  value: 'scheduled',
                  label: t('customer.checkout.schedule'),
                  description: t('customer.checkout.scheduleHint'),
                },
              ]}
              value={mode}
              onChange={(next) => {
                setMode(next);
                if (next !== 'scheduled') return;
                // A fresh schedule starts on the soonest time that works. The
                // effect above corrects it if the quote has not landed yet.
                setSlotTouched(false);
                if (!slot && slots.length > 0) setSlot(firstComfortableIn(slots) ?? slots[0]!);
              }}
            />

            {mode === 'scheduled' ? (
              <Stack gap="md">
                {/* Above the grid, not under it. The times this is about are the
                    earliest ones, and at a five-minute interval the grid runs for
                    pages — a warning below it is one nobody scrolls to. It is
                    still the customer's to take: the sentence explains, the link
                    offers the soonest that works, and Place order stays live. */}
                {slotIsTight ? (
                  <Callout
                    tone="warning"
                    size="base"
                    title={t('customer.checkout.slotTooSoonTitle')}
                    message={t('customer.checkout.slotTooSoonMessage', {
                      minutes: Math.max(1, Math.round((current?.prep_seconds ?? 0) / 60)),
                    })}
                    action={
                      suggestion !== null && suggestionLabel !== null ? (
                        <Pressable
                          accessibilityRole="button"
                          accessibilityLabel={t('customer.checkout.slotTooSoonAction', {
                            time: suggestionLabel,
                          })}
                          onPress={() => {
                            setSlot(suggestion);
                            setSlotTouched(true);
                          }}
                          className="self-start">
                          <Text className="text-warning text-sm font-semibold underline">
                            {t('customer.checkout.slotTooSoonAction', { time: suggestionLabel })}
                          </Text>
                        </Pressable>
                      ) : undefined
                    }
                  />
                ) : null}

                <SlotPicker
                  slots={slots}
                  timezone={restaurant.timezone}
                  value={slot}
                  onChange={(value) => {
                    setSlot(value);
                    setSlotTouched(true);
                  }}
                  earliest={quote.data?.earliest_scheduled_for ?? null}
                  loading={false}
                />
              </Stack>
            ) : current && !isClosed ? (
              <Text className="text-muted-foreground text-sm">
                {t('customer.checkout.estimatedReady', {
                  when: formatDayTime(
                    current.eta_ready_at,
                    restaurant.timezone,
                    undefined,
                    t,
                  ),
                })}
                {current.eta_model === 'baseline'
                  ? t('customer.checkout.estimateInitial')
                  : current.eta_sample_count > 0
                    ? t('customer.checkout.estimateLearned', {
                        count: current.eta_sample_count,
                      })
                    : ''}
              </Text>
            ) : null}

            {errors.slot ? (
              <Text className="text-destructive text-sm">{errors.slot}</Text>
            ) : null}

            {/* A closed restaurant refuses the order at the last step, and the
                refusal names the closure. It belongs to this section rather than
                beside the totals: the time is what it is about, and it reads
                here as the answer to the choice just made. */}
            {isClosed && place.error ? <ErrorBanner message={place.error} /> : null}
          </Stack>
        </Section>

        <Section
          title={t('customer.checkout.yourDetails')}
          subtitle={t('customer.checkout.yourDetailsHint')}>
          <Stack gap="lg">
            <TextField
              ref={fields.name}
              label={t('customer.checkout.nameLabel')}
              required
              value={form.name}
              onChangeText={(name) => setForm((f) => ({ ...f, name }))}
              error={errors.name}
              autoComplete="name"
              placeholder={t('customer.checkout.namePlaceholder')}
            />
            <TextField
              ref={fields.phone}
              label={t('customer.checkout.phoneLabel')}
              required
              value={form.phone}
              onChangeText={(phone) => {
                  setForm((f) => ({ ...f, phone }));
                  // Checked as it is typed, so a mistyped number is caught while
                  // it is still cheap to fix. "Too short" is held back: it is true
                  // of every number until it is finished, and saying so from the
                  // first digit is noise.
                  const parsed = phoneSchema.safeParse(phone);
                  const issue = parsed.success ? null : (parsed.error.issues[0]?.message ?? null);
                  const waitForMore = issue !== null && /too short/i.test(issue);
                  setErrors((current) => ({
                    ...current,
                    phone: issue !== null && !waitForMore ? issue : '',
                  }));
                }}
              error={errors.phone}
              keyboardType="phone-pad"
              autoComplete="tel"
              placeholder={phonePlaceholder(restaurantCountry) ?? t('customer.checkout.phonePlaceholder')}
              hint={(() => {
                  // What will be stored, and — when the shape is off for the
                  // country the order is collected in — a word about that. A
                  // hint only: nothing here refuses a number.
                  const shape = phoneHintFor(form.phone, restaurantCountry);
                  if (shape) {
                    return t(
                      shape.kind === 'foreign'
                        ? 'customer.checkout.phoneForeign'
                        : shape.kind === 'short'
                          ? 'customer.checkout.phoneShort'
                          : 'customer.checkout.phoneLong',
                      { dial: shape.dial, min: shape.min, max: shape.max },
                    );
                  }
                  // The number written the way its country writes it, so the
                  // field confirms rather than explains once something is typed.
                  return (
                    formatPhoneFor(form.phone, restaurantCountry) ??
                    t('customer.checkout.phoneHint')
                  );
                })()}
            />
            <TextField
              ref={fields.email}
              label={t('customer.checkout.emailLabel')}
              value={form.email}
              onChangeText={(email) => setForm((f) => ({ ...f, email }))}
              error={errors.email}
              keyboardType="email-address"
              autoComplete="email"
              autoCapitalize="none"
              placeholder={t('customer.checkout.emailPlaceholder')}
              hint={t('customer.checkout.emailHint')}
            />
            <TextField
              ref={fields.note}
              label={t('customer.checkout.noteLabel')}
              value={form.note}
              onChangeText={(note) => setForm((f) => ({ ...f, note }))}
              error={errors.note}
              placeholder={t('customer.checkout.notePlaceholder')}
              multiline
              numberOfLines={3}
            />
          </Stack>
        </Section>

        <Section title={t('customer.checkout.payment')}>
          <Stack gap="md">
            <StatRow label={t('common.subtotal')} value={formatMoney(displaySubtotal, currency)} numeric />
            {current && current.discount_cents > 0 ? (
              <StatRow
                label={t('common.discount')}
                value={`−${formatMoney(current.discount_cents, currency)}`}
                tone="success"
                numeric
              />
            ) : null}
            {current && current.tax_cents > 0 ? (
              <StatRow label={t('common.tax')} value={formatMoney(current.tax_cents, currency)} numeric />
            ) : null}
            <View className="border-border border-t pt-3">
              <StatRow
                label={t('common.total')}
                value={formatMoney(displayTotal, currency)}
                emphasis
                numeric
                size="lg"
              />
            </View>

            <Callout
              tone="info"
              title={t('customer.checkout.payAtCounter')}
              message={t('customer.checkout.payAtCounterHint')}
              size="base"
            />

            {!isClosed && place.error ? <ErrorBanner message={place.error} /> : null}
          </Stack>
        </Section>
      </Stack>
    </Screen>
  );
}
